import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { Dialog } from "@angular/cdk/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faSearch, faPlus, faTrash, faSpinner } from "@fortawesome/pro-light-svg-icons";
import {
  MailgunSuppression,
  MailgunSuppressionRequest
} from "../../../models/mailgun-suppression.model";
import { MailgunSuppressionService } from "../../../services/mailgun-suppression.service";
import { ConfirmationModalComponent } from "../../confirmation-modal/confirmation-modal.component";
import { ConfirmationModalData } from "../../../models/confirmation-modal-data.model";
import { ToastService } from "../../../services/toast.service";
import { StateManagementService } from "../../../services/state-management.service";

interface DomainOption {
  key: string;
  label: string;
  selected: boolean;
}

@Component({
  selector: "app-mailgun-suppression",
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule],
  templateUrl: "./mailgun-suppression.component.html"
})
export class MailgunSuppressionComponent implements OnInit, OnDestroy {
  protected readonly faSearch: IconDefinition = faSearch;
  protected readonly faPlus: IconDefinition = faPlus;
  protected readonly faTrash: IconDefinition = faTrash;
  protected readonly faSpinner: IconDefinition = faSpinner;

  protected searchEmail: string = "";
  protected addEmail: string = "";
  protected suppressions: MailgunSuppression[] = [];
  protected isSearching: boolean = false;
  protected isAdding: boolean = false;
  protected hasSearched: boolean = false;
  protected showAddPanel: boolean = false;

  protected readonly domainOptions: DomainOption[] = [
    { key: "AGENTIUM", label: "mg.agentium.com", selected: false },
    { key: "INSURANCE_DIRECTORY", label: "mg.insurancedirectory.com", selected: false },
    { key: "INSURED_BETTER", label: "mg.insuredbetter.com", selected: false },
    { key: "MOMENTUM_EDGE", label: "mg.momentumedge.com", selected: false },
    { key: "TRUSTED_CHOICE", label: "mg.trustedchoice.com", selected: false }
  ];

  private readonly onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private mailgunSuppressionService: MailgunSuppressionService,
    private dialog: Dialog,
    private toastService: ToastService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.stateManagementService.setPageTitle("Mailgun Suppressions | Momentum Edge");
    this.stateManagementService.pageHeading.set("Mailgun Suppressions");
  }

  protected search(): void {
    if (!this.searchEmail.trim()) {
      return;
    }
    this.isSearching = true;
    this.hasSearched = false;
    this.mailgunSuppressionService
      .searchSuppressions(this.searchEmail.trim())
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (results: MailgunSuppression[]): void => {
          this.suppressions = results;
          this.hasSearched = true;
          this.isSearching = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.isSearching = false;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to search suppressions."),
            "Close",
            {
              duration: 7000,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  protected openAddPanel(): void {
    this.addEmail = this.searchEmail;
    this.domainOptions.forEach((d: DomainOption): void => {
      d.selected = false;
    });
    this.showAddPanel = true;
  }

  protected cancelAdd(): void {
    this.showAddPanel = false;
  }

  protected confirmAdd(): void {
    const selectedDomains: string[] = this.domainOptions
      .filter((d: DomainOption): boolean => d.selected)
      .map((d: DomainOption): string => d.key);

    if (!this.addEmail.trim() || selectedDomains.length === 0) {
      return;
    }

    const domainLabels: string = this.domainOptions
      .filter((d: DomainOption): boolean => d.selected)
      .map((d: DomainOption): string => d.label)
      .join(", ");

    const data: ConfirmationModalData = {
      title: "Add Suppression",
      message: `Are you sure you want to add a suppression for ${this.addEmail.trim()} on: ${domainLabels}?`,
      messageHtml: `Are you sure you want to add a suppression for <strong>${this.addEmail.trim()}</strong> on: <strong>${domainLabels}</strong>?`,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel"
    };

    this.dialog
      .open<boolean>(ConfirmationModalComponent, {
        panelClass: "centered-dialog",
        hasBackdrop: true,
        data
      })
      .closed.pipe(takeUntil(this.onDestroy$))
      .subscribe((confirmed: boolean | undefined): void => {
        if (confirmed) {
          this.executeAdd(this.addEmail.trim(), selectedDomains);
        }
      });
  }

  private executeAdd(emailAddress: string, domains: string[]): void {
    this.isAdding = true;
    const request: MailgunSuppressionRequest = { emailAddress, domains };
    this.mailgunSuppressionService
      .addSuppression(request)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (): void => {
          this.isAdding = false;
          this.showAddPanel = false;
          this.toastService.open(`Suppression added successfully for ${emailAddress}.`, "Close", {
            duration: 7000,
            panelClass: ["bg-green-600"]
          });
          if (this.searchEmail.trim()) {
            this.search();
          }
        },
        error: (error: HttpErrorResponse): void => {
          this.isAdding = false;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to add suppression."),
            "Close",
            {
              duration: 7000,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  protected confirmRemove(suppression: MailgunSuppression): void {
    const data: ConfirmationModalData = {
      title: "Remove Suppression",
      message: `Are you sure you want to remove the suppression for ${suppression.emailAddress} on ${suppression.domain}?`,
      messageHtml: `Are you sure you want to remove the suppression for <strong>${suppression.emailAddress}</strong> on <strong>${suppression.domain}</strong>?`,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel"
    };

    this.dialog
      .open<boolean>(ConfirmationModalComponent, {
        panelClass: "centered-dialog",
        hasBackdrop: true,
        data
      })
      .closed.pipe(takeUntil(this.onDestroy$))
      .subscribe((confirmed: boolean | undefined): void => {
        if (confirmed) {
          this.executeRemove(suppression);
        }
      });
  }

  private executeRemove(suppression: MailgunSuppression): void {
    const domainKey: string = this.getDomainKey(suppression.domain);
    this.mailgunSuppressionService
      .removeSuppression(suppression.emailAddress, domainKey)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (): void => {
          this.suppressions = this.suppressions.filter(
            (s: MailgunSuppression): boolean =>
              !(s.emailAddress === suppression.emailAddress && s.domain === suppression.domain)
          );
          this.toastService.open(
            `Suppression removed for ${suppression.emailAddress} on ${suppression.domain}.`,
            "Close",
            { duration: 7000, panelClass: ["bg-green-600"] }
          );
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to remove suppression."),
            "Close",
            {
              duration: 7000,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  private getDomainKey(domainLabel: string): string {
    const match: DomainOption | undefined = this.domainOptions.find(
      (d: DomainOption): boolean => d.label === domainLabel
    );
    return match ? match.key : domainLabel;
  }

  private extractErrorMessage(error: HttpErrorResponse, fallback: string): string {
    if (error.error && typeof error.error === "object" && error.error.message) {
      return error.error.message;
    }
    if (typeof error.error === "string" && error.error.trim()) {
      return error.error;
    }
    if (error.message) {
      return error.message;
    }
    return fallback;
  }

  protected get anyDomainSelected(): boolean {
    return this.domainOptions.some((d: DomainOption): boolean => d.selected);
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
